/**
 * Read models. Every screen that lists people goes through here, so "expired",
 * "outstanding" and "due for renewal" mean exactly one thing across the app.
 *
 * These load whole tables and join in memory. At gym scale — a few thousand
 * members and maybe ten thousand payments — that is a handful of milliseconds
 * and it keeps the logic pure and testable. If the academy ever outgrows it,
 * the fix is indexed range queries here, with no screen changes.
 */
import { db, live } from './db';
import { dropVoidedPayments } from '@shared/voids';
import { daysRemaining, isWithin, monthBounds, todayISO } from '@shared/dates';
import {
  computeAmountPaid,
  computeSubscriptionRemaining,
  deriveStatus,
} from '@shared/pricing';
import { normalizePhone, normalizeSkIdQuery } from '@shared/identity';
import { classLabel } from '@shared/catalog';
import { activeFreeze, freezeUsage, liveFreezes } from '@shared/freezes';
import { annotateWalkInVisits, type WalkInVisit } from '@shared/walkins';
import type {
  Freeze,
  ISODate,
  Member,
  Payment,
  Subscription,
  SubscriptionStatus,
  WalkIn,
} from '@shared/types';

/** A subscription with everything a row needs to render. */
export interface SubscriptionRow {
  subscription: Subscription;
  member: Member;
  status: SubscriptionStatus;
  daysRemaining: number;
  amountPaid: number;
  remaining: number;
  /** Live freezes on this subscription, oldest first. */
  freezes: Freeze[];
  /** The freeze covering today, if the member is paused right now. */
  frozenUntil: ISODate | null;
  /** Whether another freeze is still allowed, and for how long. */
  freezesLeft: number;
  freezeDaysAvailable: number;
}

/** A member with their headline subscription and total balance. */
export interface MemberRow {
  member: Member;
  /** The subscription that best represents them now — latest end date. */
  current: SubscriptionRow | null;
  /** Sum of what is owed across ALL their subscriptions. */
  totalOutstanding: number;
  subscriptionCount: number;
}

/** Everything needed to render one member's profile. */
export interface MemberProfile {
  member: Member;
  subscriptions: SubscriptionRow[];
  payments: Payment[];
  totalOutstanding: number;
}

interface Snapshot {
  members: Member[];
  subscriptions: Subscription[];
  payments: Payment[];
  freezes: Freeze[];
}

async function loadSnapshot(): Promise<Snapshot> {
  const [members, subscriptions, payments, freezes] = await Promise.all([
    db.members.toArray(),
    db.subscriptions.toArray(),
    db.payments.toArray(),
    db.freezes.toArray(),
  ]);
  // Voided subscriptions take their payments out of every total with them.
  // Filtering here rather than at each call site means a figure added later
  // cannot silently miss it — see shared/voids.ts.
  const liveSubscriptions = live(subscriptions);
  return {
    members: live(members),
    subscriptions: liveSubscriptions,
    payments: dropVoidedPayments(live(payments), liveSubscriptions),
    freezes: live(freezes),
  };
}

/**
 * Payments that still count towards money.
 *
 * NEVER read `db.payments` directly for a revenue figure. A voided
 * subscription is soft-deleted, but its payments are not — deliberately, so
 * that `payments` can keep having no UPDATE and no DELETE policy in Postgres
 * and a void stays reversible. That leaves the rows sitting in the table with
 * nothing to attribute them to, and any total summed straight off the table
 * silently includes revenue for a sale that never happened.
 */
async function countablePayments(): Promise<Payment[]> {
  const [payments, subscriptions] = await Promise.all([
    db.payments.toArray(),
    db.subscriptions.toArray(),
  ]);
  return dropVoidedPayments(live(payments), live(subscriptions));
}

/** Group freezes by the subscription they pause. */
function freezesBySubscription(freezes: Freeze[]): Map<string, Freeze[]> {
  const map = new Map<string, Freeze[]>();
  for (const f of freezes) {
    const list = map.get(f.subscription_id);
    if (list) list.push(f);
    else map.set(f.subscription_id, [f]);
  }
  return map;
}

/** Group payments by the subscription they belong to. */
function paymentsBySubscription(payments: Payment[]): Map<string, Payment[]> {
  const map = new Map<string, Payment[]>();
  for (const p of payments) {
    const list = map.get(p.subscription_id);
    if (list) list.push(p);
    else map.set(p.subscription_id, [p]);
  }
  return map;
}

function buildRow(
  subscription: Subscription,
  member: Member,
  payments: Payment[],
  today: ISODate,
  freezes: Freeze[] = []
): SubscriptionRow {
  const amountPaid = computeAmountPaid(payments);
  const live = liveFreezes(freezes) as Freeze[];
  const current = activeFreeze(live, today);
  const usage = freezeUsage(subscription, live);

  return {
    subscription,
    member,
    // A frozen member is not expiring — their end date already moved.
    status: deriveStatus(subscription, today, Boolean(current)),
    daysRemaining: daysRemaining(subscription.end_date, today),
    amountPaid,
    // NOT total_due − paid: a cancelled subscription owes only the sessions
    // used, or the refund it was given would read back as an unpaid balance.
    remaining: computeSubscriptionRemaining(subscription, amountPaid),
    freezes: live,
    frozenUntil: current?.end_date ?? null,
    freezesLeft: usage.remainingFreezes,
    freezeDaysAvailable: usage.daysAvailable,
  };
}

/* -------------------------------------------------------------------------
   Members list
   ------------------------------------------------------------------------- */

export async function loadMemberRows(today: ISODate = todayISO()): Promise<MemberRow[]> {
  const { members, subscriptions, payments, freezes } = await loadSnapshot();
  const byMember = new Map<string, Member>(members.map((m) => [m.id, m]));
  const paid = paymentsBySubscription(payments);
  const frozen = freezesBySubscription(freezes);

  const rowsByMember = new Map<string, SubscriptionRow[]>();
  for (const sub of subscriptions) {
    const member = byMember.get(sub.member_id);
    if (!member) continue; // orphan — member soft-deleted
    const row = buildRow(sub, member, paid.get(sub.id) ?? [], today, frozen.get(sub.id) ?? []);
    const list = rowsByMember.get(sub.member_id);
    if (list) list.push(row);
    else rowsByMember.set(sub.member_id, [row]);
  }

  return members
    .map((member) => {
      const rows = rowsByMember.get(member.id) ?? [];
      // "Current" = the one running furthest into the future. Cancelled
      // subscriptions never represent a member, but still count for balances.
      const candidates = rows.filter((r) => r.status !== 'cancelled');
      const current =
        candidates.length > 0
          ? candidates.reduce((best, r) =>
              r.subscription.end_date > best.subscription.end_date ? r : best
            )
          : null;

      return {
        member,
        current,
        totalOutstanding: rows.reduce((sum, r) => sum + r.remaining, 0),
        subscriptionCount: rows.length,
      };
    })
    .sort((a, b) => a.member.full_name.localeCompare(b.member.full_name, ['ar', 'en']));
}

/**
 * Free-text search over name, phone and SK ID.
 *
 * Each is matched in the form a human would type it: "42" and "sk 42" both
 * find SK-0042, and a phone matches however it was punctuated.
 */
export function filterMemberRows(rows: MemberRow[], query: string): MemberRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;

  const asSkId = normalizeSkIdQuery(q);
  const asPhone = normalizePhone(q);

  return rows.filter(({ member }) => {
    if (member.full_name.toLowerCase().includes(q)) return true;
    if (member.sk_id.toLowerCase().includes(q)) return true;
    if (asSkId && member.sk_id === asSkId) return true;
    if (asPhone.length >= 3 && member.phone.includes(asPhone)) return true;
    if (member.notes.toLowerCase().includes(q)) return true;
    return false;
  });
}

/**
 * Narrow the members list to one class.
 *
 * Matches against ANY of the member's subscriptions, not just their headline
 * one — someone whose latest term is judo but who also trains kickboxing must
 * still appear under kickboxing.
 */
export function filterMemberRowsByClass(
  rows: MemberRow[],
  classKey: string,
  allRowsByMember: Map<string, string[]>
): MemberRow[] {
  if (classKey === 'all') return rows;
  return rows.filter((r) => (allRowsByMember.get(r.member.id) ?? []).includes(classKey));
}

/** class keys each member is subscribed to, for the class filter. */
export async function loadMemberClassIndex(): Promise<Map<string, string[]>> {
  const subs = live(await db.subscriptions.toArray());
  const map = new Map<string, string[]>();
  for (const s of subs) {
    const list = map.get(s.member_id);
    if (list) {
      if (!list.includes(s.class_key)) list.push(s.class_key);
    } else {
      map.set(s.member_id, [s.class_key]);
    }
  }
  return map;
}

export function filterMemberRowsByStatus(
  rows: MemberRow[],
  status: SubscriptionStatus | 'all' | 'due'
): MemberRow[] {
  if (status === 'all') return rows;
  if (status === 'due') return rows.filter((r) => r.totalOutstanding > 0);
  return rows.filter((r) => r.current?.status === status);
}

/* -------------------------------------------------------------------------
   Member profile
   ------------------------------------------------------------------------- */

export async function loadMemberProfile(
  memberId: string,
  today: ISODate = todayISO()
): Promise<MemberProfile | null> {
  const member = await db.members.get(memberId);
  if (!member || member.deleted_at !== null) return null;

  const [subs, pays, frz] = await Promise.all([
    db.subscriptions.where('member_id').equals(memberId).toArray(),
    db.payments.where('member_id').equals(memberId).toArray(),
    db.freezes.where('member_id').equals(memberId).toArray(),
  ]);

  const subscriptions = live(subs);
  // A voided subscription's payments must leave the member's history too, not
  // just the totals. A row still sitting here for a sale that never happened
  // is exactly what someone reading this screen would take as proof it did.
  const payments = dropVoidedPayments(live(pays), subscriptions);
  const paid = paymentsBySubscription(payments);
  const frozen = freezesBySubscription(live(frz));

  const rows = subscriptions
    .map((s) => buildRow(s, member, paid.get(s.id) ?? [], today, frozen.get(s.id) ?? []))
    // Newest term first — the one staff will act on is at the top.
    .sort((a, b) => b.subscription.start_date.localeCompare(a.subscription.start_date));

  return {
    member,
    subscriptions: rows,
    payments: payments.sort((a, b) => b.paid_at.localeCompare(a.paid_at)),
    totalOutstanding: rows.reduce((sum, r) => sum + r.remaining, 0),
  };
}

/* -------------------------------------------------------------------------
   Renewals
   ------------------------------------------------------------------------- */

export interface RenewalBuckets {
  expiringSoon: SubscriptionRow[];
  recentlyExpired: SubscriptionRow[];
  longExpired: SubscriptionRow[];
}

/** Expired within this many days counts as "recent" and worth chasing. */
export const RECENTLY_EXPIRED_DAYS = 30;

/**
 * Who to call, in three buckets, each sorted by end date ascending.
 *
 * Only the LATEST subscription per member-and-class is considered. Without
 * that, renewing someone would leave their old term sitting in the list
 * forever and the screen would slowly fill with people who already paid.
 */
export async function loadRenewalBuckets(
  today: ISODate = todayISO()
): Promise<RenewalBuckets> {
  const { members, subscriptions, payments, freezes } = await loadSnapshot();
  const byMember = new Map<string, Member>(members.map((m) => [m.id, m]));
  const paid = paymentsBySubscription(payments);
  const frozen = freezesBySubscription(freezes);

  const latest = new Map<string, Subscription>();
  for (const sub of subscriptions) {
    if (sub.cancelled) continue;
    const key = `${sub.member_id}::${sub.class_key}`;
    const held = latest.get(key);
    if (!held || sub.end_date > held.end_date) latest.set(key, sub);
  }

  const buckets: RenewalBuckets = {
    expiringSoon: [],
    recentlyExpired: [],
    longExpired: [],
  };

  for (const sub of latest.values()) {
    const member = byMember.get(sub.member_id);
    if (!member) continue;
    const row = buildRow(sub, member, paid.get(sub.id) ?? [], today, frozen.get(sub.id) ?? []);
    const days = row.daysRemaining;

    // A member on a freeze is not lapsing — do not send staff chasing someone
    // who is on holiday with the gym's blessing.
    if (row.status === 'frozen') continue;

    if (days >= 0 && days <= 7) buckets.expiringSoon.push(row);
    else if (days < 0 && days >= -RECENTLY_EXPIRED_DAYS) buckets.recentlyExpired.push(row);
    else if (days < -RECENTLY_EXPIRED_DAYS) buckets.longExpired.push(row);
    // More than 7 days left: nothing to chase.
  }

  const byEndDate = (a: SubscriptionRow, b: SubscriptionRow) =>
    a.subscription.end_date.localeCompare(b.subscription.end_date);

  buckets.expiringSoon.sort(byEndDate);
  buckets.recentlyExpired.sort(byEndDate);
  buckets.longExpired.sort(byEndDate);

  return buckets;
}

/* -------------------------------------------------------------------------
   Dashboard
   ------------------------------------------------------------------------- */

export interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  expiringSoon: number;
  expired: number;
  /** Subscription payments + walk-ins taken this calendar month. */
  collectedThisMonth: number;
  collectedFromSubscriptions: number;
  collectedFromWalkIns: number;
  walkInsThisMonth: number;
  /** Everything still owed, across every subscription. */
  totalOutstanding: number;
  membersOwing: number;
}

/* -------------------------------------------------------------------------
   Walk-in history
   ------------------------------------------------------------------------- */

export type WalkInHistoryRow = WalkIn & WalkInVisit;

/**
 * Every walk-in, newest first, annotated with which visit this is for the
 * person and how many they have made in total — see `annotateWalkInVisits`.
 * Nothing is capped here: "has this person been in before" needs the WHOLE
 * history. The screen decides how much of it to render (see
 * WalkInHistory.tsx).
 *
 * This is also the list a mistyped drop-in is found and voided from, so
 * voided rows must not appear — one still listed can be voided twice.
 */
export async function loadWalkInHistory(): Promise<WalkInHistoryRow[]> {
  const rows = live(await db.walk_ins.toArray());
  return annotateWalkInVisits(rows).sort((a, b) => {
    const byDate = b.visited_at.localeCompare(a.visited_at);
    // Several drop-ins share a date, so fall back to creation order —
    // otherwise the newest one is not reliably at the top, which is exactly
    // where someone correcting what they just typed will look.
    return byDate !== 0 ? byDate : b.created_at.localeCompare(a.created_at);
  });
}

/** Narrow walk-in rows to an inclusive date range. */
export function filterWalkInRowsByRange<T extends { visited_at: ISODate }>(
  rows: T[],
  from: ISODate,
  to: ISODate
): T[] {
  return rows.filter((r) => isWithin(r.visited_at, from, to));
}

/** Narrow walk-in rows to one class. */
export function filterWalkInRowsByClass<T extends { class_key: string }>(
  rows: T[],
  classKey: string
): T[] {
  if (classKey === 'all') return rows;
  return rows.filter((r) => r.class_key === classKey);
}

/** Free-text search over name and phone — same normalisation as the members list. */
export function filterWalkInRows<T extends { person_name: string; phone: string }>(
  rows: T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const asPhone = normalizePhone(q);
  return rows.filter(
    (r) =>
      r.person_name.toLowerCase().includes(q) ||
      (asPhone.length >= 3 && normalizePhone(r.phone).includes(asPhone))
  );
}

/** How many walk-in rows sit in each class, for the class filter chips. */
export function countWalkInRowsByClass<T extends { class_key: string }>(
  rows: readonly T[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const r of rows) counts[r.class_key] = (counts[r.class_key] ?? 0) + 1;
  return counts;
}

/**
 * The five dashboard numbers.
 *
 * "Collected this month" deliberately includes walk-ins — it answers "how much
 * money came through the door", and a front desk that took 40 drop-ins would
 * otherwise see a figure that does not match the till.
 */
export async function loadDashboardStats(
  today: ISODate = todayISO()
): Promise<DashboardStats> {
  const [rows, walkIns, payments] = await Promise.all([
    loadMemberRows(today),
    db.walk_ins.toArray(),
    countablePayments(),
  ]);

  const { from, to } = monthBounds(today);

  const stats: DashboardStats = {
    totalMembers: rows.length,
    activeMembers: 0,
    expiringSoon: 0,
    expired: 0,
    collectedThisMonth: 0,
    collectedFromSubscriptions: 0,
    collectedFromWalkIns: 0,
    walkInsThisMonth: 0,
    totalOutstanding: 0,
    membersOwing: 0,
  };

  for (const row of rows) {
    if (row.current?.status === 'active') stats.activeMembers++;
    else if (row.current?.status === 'expiring_soon') stats.expiringSoon++;
    else if (row.current?.status === 'expired') stats.expired++;

    stats.totalOutstanding += row.totalOutstanding;
    if (row.totalOutstanding > 0) stats.membersOwing++;
  }

  for (const p of live(payments)) {
    if (isWithin(p.paid_at, from, to)) stats.collectedFromSubscriptions += p.amount_piastres;
  }

  for (const w of live(walkIns)) {
    if (isWithin(w.visited_at, from, to)) {
      stats.collectedFromWalkIns += w.amount_piastres;
      stats.walkInsThisMonth++;
    }
  }

  stats.collectedThisMonth = stats.collectedFromSubscriptions + stats.collectedFromWalkIns;
  return stats;
}

/**
 * Narrow subscription rows to one class.
 *
 * Unlike the members list, this needs no lookup index: a renewal row IS a
 * subscription, so its class is right there on the row.
 */
export function filterSubscriptionRowsByClass(
  rows: SubscriptionRow[],
  classKey: string
): SubscriptionRow[] {
  if (classKey === 'all') return rows;
  return rows.filter((r) => r.subscription.class_key === classKey);
}

/** How many renewal rows sit in each class, for the filter chips. */
export function countRowsByClass(rows: readonly SubscriptionRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const key = r.subscription.class_key;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/** Search across renewal rows — same fields as the members list. */
export function filterSubscriptionRows(
  rows: SubscriptionRow[],
  query: string
): SubscriptionRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const asPhone = normalizePhone(q);
  return rows.filter(
    (r) =>
      r.member.full_name.toLowerCase().includes(q) ||
      r.member.sk_id.toLowerCase().includes(q) ||
      classLabel(r.subscription.class_key).toLowerCase().includes(q) ||
      (asPhone.length >= 3 && r.member.phone.includes(asPhone))
  );
}
